import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import SessionCoordinator from '../../layouts/SessionCoordinator';
import ToastCenter from '../../shared/components/ToastCenter';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import OperationBlockGate from '../../features/operation-blocks/components/OperationBlockGate';
import { LoginRoute, HomeRedirect, SecuredPage } from './guards';
import { routeConfig } from './routeConfig';

// Resolve lazy components at module scope so React.lazy is called once per route.
const resolvedRoutes = routeConfig.map((route) => ({
  ...route,
  Component: route.lazy ? lazy(route.lazy) : null,
}));

const RouteElement = ({ route }) => {
  const { Component } = route;

  if (route.type === 'login') {
    return (
      <LoginRoute>
        <Component />
      </LoginRoute>
    );
  }

  if (route.type === 'public') {
    return <Component />;
  }

  if (route.type === 'redirect') {
    return (
      <SecuredPage permission={route.permission} title="Settings">
        <Navigate to={route.to} replace />
      </SecuredPage>
    );
  }

  if (route.type === 'home') {
    return <HomeRedirect />;
  }

  if (route.type === 'secured') {
    return (
      <SecuredPage
        permission={route.permission}
        allowedRoles={route.allowedRoles}
        requireSociety={route.requireSociety}
        requireSocietyForRoles={route.requireSocietyForRoles}
        featureFlag={route.featureFlag}
        title={route.title}
      >
        <Component />
      </SecuredPage>
    );
  }

  return null;
};

const preloadDashboardRoutes = () => {
  routeConfig.forEach((route) => {
    if ((route.type === 'secured' || route.type === 'redirect') && route.lazy) {
      route.lazy().catch(() => {});
    }
  });
};

const AppRouter = () => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if ('requestIdleCallback' in window) {
      const preloadId = window.requestIdleCallback(preloadDashboardRoutes, { timeout: 1500 });
      return () => window.cancelIdleCallback(preloadId);
    }

    const preloadId = window.setTimeout(preloadDashboardRoutes, 300);
    return () => window.clearTimeout(preloadId);
  }, []);

  return (
    <Router>
      <SessionCoordinator />
      <OperationBlockGate>
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Routes>
              {resolvedRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={<RouteElement route={route} />} />
              ))}
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </OperationBlockGate>
      <ToastCenter />
    </Router>
  );
};

export default AppRouter;
